import { isUndefinedOrEmpty, reverse } from '@afterpay/utils/string'
import { ShippingOption, ShippingOptionUpdate } from '@afterpaytouch/portal-api/types'
import { ShippingOptionErrorType, ShippingOptionUpdateValidation, ShippingOptionValidation, ShippingOptionValidationError } from '../model'

import { Money } from '@afterpay/types'

interface NameValidation {
  name: string
  optional?: boolean
}

interface AddressValidation {
  input: string
  optional?: boolean
}

const VALID_CHARACTERS_REGEX = /^$|^[a-zA-Z\u00C0-\u017F][a-zA-Z\u00C0-\u017F- ']*$/
const INVALID_NAMES = ['registered', 'guest', 'user']

export const isValidName = ({ name, optional = false }: NameValidation): boolean => {
  if (isUndefinedOrEmpty(name)) {
    return optional
  }
  const isValidCharacters = VALID_CHARACTERS_REGEX.test(name)
  const isValidUser = !INVALID_NAMES.includes(name.toLowerCase())
  return isValidCharacters && isValidUser
}

export const isValidAddressComponent = ({ input, optional = false }: AddressValidation): boolean => {
  return isValidAddressInput(input, optional)
}

export const isValidAddressInput = (input, optional = false): boolean => {
  if (optional && isUndefinedOrEmpty(input)) {
    return true
  }
  if (isUndefinedOrEmpty(input)) {
    return false
  }

  return true
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isAmount = (value?: any): value is Money => {
  const AMOUNT_REGEX = /^[0-9]+(\.[0-9]{1,2})?$/
  return value !== undefined && typeof value.amount === 'string' && typeof value.currency === 'string' && AMOUNT_REGEX.test(value.amount)
}

const isEqualToOrderCurrency = (targetAmount: Money, orderAmount: Money): boolean => {
  return targetAmount !== undefined && typeof targetAmount.currency === 'string' && targetAmount.currency === orderAmount.currency
}

const MAX_SHIPPING_OPTION_ID_LENGTH = 128

export const isValidShippingOptions = (options: ShippingOption[], orderAmount: Money, isPickup: boolean = false): ShippingOptionValidation | undefined => {
  const MIN_SHIPPING_OPTIONS = 1
  const MAX_SHIPPING_OPTIONS = isPickup ? 1 : 5

  const validations: Array<{ guard: () => boolean; result: ShippingOptionValidationError }> = [
    {
      guard: () => !Array.isArray(options),
      result: {
        severity: 'error',
        message: 'Shipping options must be an array',
      },
    },
    {
      guard: () => options.length < MIN_SHIPPING_OPTIONS,
      result: {
        severity: 'error',
        message: 'Shipping options array must have at least one option.',
      },
    },
    {
      guard: () => options.length !== Array.from(new Set(options.map((option) => option.id))).length,
      result: {
        severity: 'error',
        message: 'Shipping options array IDs must be unique.',
      },
    },
    {
      guard: () =>
        !options.reduce(
          (total, option) => total && option.id !== undefined && option.name !== undefined && isAmount(option.shippingAmount) && isAmount(option.orderAmount),
          true
        ),
      result: {
        severity: 'error',
        message: 'Shipping options must conform to specifications.',
      },
    },
    {
      guard: () => !options.map((option) => option.id.length <= MAX_SHIPPING_OPTION_ID_LENGTH).reduce((total, value) => total && value, true),
      result: {
        severity: 'error',
        message: `Shipping options ID length must be <= ${MAX_SHIPPING_OPTION_ID_LENGTH} characters.`,
      },
    },
    {
      guard: () =>
        !options.reduce(
          (total, option) => total && isEqualToOrderCurrency(option.shippingAmount, orderAmount) && isEqualToOrderCurrency(option.orderAmount, orderAmount),
          true
        ),
      result: {
        severity: 'error',
        message: 'Shipping options must be in the same currency as the order.',
      },
    },
    {
      guard: () => !options.reduce((total, option) => total && (option.taxAmount == null || isAmount(option.taxAmount)), true),
      result: {
        severity: 'error',
        message: 'Tax amounts must conform to specifications when provided.',
      },
    },
    {
      guard: () => !options.reduce((total, option) => total && (option.taxAmount == null || isEqualToOrderCurrency(option.taxAmount, orderAmount)), true),
      result: {
        severity: 'error',
        message: 'Tax amounts must be in the same currency as the order.',
      },
    },
    {
      guard: () => MAX_SHIPPING_OPTIONS < options.length,
      result: {
        severity: 'warn',
        message: `Shipping options array truncated to ${MAX_SHIPPING_OPTIONS} options.`,
      },
    },
  ]

  for (const validation of validations) {
    if (validation.guard()) {
      if (validation.result.severity === 'warn') {
        return { ...validation.result, allShippingOptions: options.slice(0, MAX_SHIPPING_OPTIONS) }
      }
      return validation.result
    }
  }
}

export const isValidShippingOptionUpdate = (option: ShippingOptionUpdate, orderAmount: Money): ShippingOptionUpdateValidation | undefined => {
  if (option === undefined) {
    return undefined
  }

  const validations: Array<{ guard: () => boolean; result: ShippingOptionValidationError }> = [
    {
      guard: () => option.id === undefined || !isAmount(option.shippingAmount) || !isAmount(option.orderAmount),
      result: {
        severity: 'error',
        message: 'Shipping option must conform to specification.',
        errorCode: ShippingOptionErrorType.ShippingOptionUpdateInvalid,
      },
    },
    {
      guard: () => option.taxAmount != null && !isAmount(option.taxAmount),
      result: {
        severity: 'error',
        message: 'Tax amount must conform to specification when provided.',
        errorCode: ShippingOptionErrorType.ShippingOptionUpdateInvalid,
      },
    },
    {
      guard: () => option.id.length > MAX_SHIPPING_OPTION_ID_LENGTH,
      result: {
        severity: 'error',
        message: `Shipping option ID length must be <= ${MAX_SHIPPING_OPTION_ID_LENGTH} characters.`,
        errorCode: ShippingOptionErrorType.ShippingOptionUpdateInvalid,
      },
    },
    {
      guard: () => !isEqualToOrderCurrency(option.shippingAmount, orderAmount) || !isEqualToOrderCurrency(option.orderAmount, orderAmount),
      result: {
        severity: 'error',
        message: 'Shipping options must be in the same currency as the order.',
        errorCode: ShippingOptionErrorType.ShippingOptionUpdateInvalid,
      },
    },
    {
      guard: () => option.taxAmount != null && !isEqualToOrderCurrency(option.taxAmount, orderAmount),
      result: {
        severity: 'error',
        message: 'Tax amount must be in the same currency as the order.',
        errorCode: ShippingOptionErrorType.ShippingOptionUpdateInvalid,
      },
    },
    {
      guard: () => option.taxAmount === undefined,
      result: {
        severity: 'error',
        message: 'Tax amount must be provided.',
        errorCode: ShippingOptionErrorType.ShippingOptionUpdateInvalid,
      },
    },
  ]

  for (const validation of validations) {
    if (validation.guard()) {
      return validation.result
    }
  }
}

export const isValidEmail = (email: string): email is string => {
  if (isUndefinedOrEmpty(email)) {
    return false
  }
  const emailRegex = /\S+@\S+\.\S+$/
  const reversedEmail = reverse(email)
  const startWithDotRegex = /^(?!\.)\S+/
  return emailRegex.test(email) && startWithDotRegex.test(reversedEmail) && !email.split('@')[0].includes('*')
}

export const valueIsNotNullOrUndefined = (value: unknown): boolean => {
  return value !== null && value !== undefined
}
